import {normalize, schema} from 'normalizr'
import {camelizeKeys} from 'humps'
import * as Errors from '../constants/Errors'
import {getSandboxModeFlag} from "../selectors/GetParam";
import {setCookie} from "../util/util";

const isSandboxMode = getSandboxModeFlag()
const API_ROOT_DEFAULT = isSandboxMode ? 'https://api-sandbox.mittwald.de/v1/' : 'https://api.mittwald.de/v1/'


const reseller = new schema.Entity('reseller', {}, {
    idAttribute: (value, parent, key) => {
        return value.uid
    }
})

const account = new schema.Entity('account', {}, {
    idAttribute: (value, parent, key) => {
        return value.uid
    }
})

const contract = new schema.Entity('contract', {}, {
    idAttribute: (value, parent, key) => {
        return value.uid
    }
})

const articleOption = new schema.Entity('articleOption', {}, {
    idAttribute: (value, parent, key) => {
        return value.default ? ('default_' + key) : value.article ? "" + value.article.articleNumber : "virtual"
    }
})

const articleOptionCollection = new schema.Entity('articleOptionCollection', {
    hardware: {
        "hddSw": [articleOption]
    },
    paidSoftware: {
        "elasticsearch": [articleOption],
        "varnish": [articleOption],
        "node": [articleOption],
    }
}, {
    idAttribute: (value, parent, key) => {
        return "" + parent.articleNumber
    }
})

const baseArticleSchema = new schema.Entity('article', {
    articleOptions: articleOptionCollection
}, {idAttribute: articleNumber => 'baseArticle'})


const articleOptionDataSchema = new schema.Entity('articleOption', {
    articleOptions: articleOptionCollection
}, {
    idAttribute: (value, parent, key) => {
        return "" + value.articleNumber
    }
})

const articleOptionsDataSchema = [articleOptionDataSchema]

const bearerTokenSchema = new schema.Entity('tokens', {}, {idAttribute: token => "token"})
const topLevelDomainSchema = new schema.Entity('topLevelDomains', {}, {idAttribute: 'name'})
const topLevelDomainsSchema = new schema.Array(topLevelDomainSchema)
const domainCheckSchema = new schema.Entity('domainAvailabiltiy')
const cartSchema = new schema.Object('cart')

const customerCategorySchema = new schema.Entity('customerCategory', {}, {idAttribute: 'uid'})
const customerCategoriesSchema = new schema.Array(customerCategorySchema)

const softwareItemSchema = new schema.Entity('software', {}, {idAttribute: 'identifier'})
const softwareDataSchema = new schema.Array(softwareItemSchema)

const databaseItemSchema = new schema.Entity('database', {}, {idAttribute: 'uid'})
const databaseDataSchema = new schema.Array(databaseItemSchema)

const operatingSystemItemSchema = new schema.Entity('operatingSystem', {}, {idAttribute: 'identifier'})
const operatingSystemsDataSchema = new schema.Array(operatingSystemItemSchema)

const promotionCodeSchema = new schema.Object('promotionCode')
const bankAccountSchema = new schema.Object('bankAccountSchema')

const jsonSchema = new schema.Object('json')

const accountSchema = [account];
const contractSchema = [contract];
const resellerSchema = [reseller];

export const Schemas = {
    BASE_ARTICLE: baseArticleSchema,
    ARTICLE_OPTION_DATA: articleOptionsDataSchema,
    ARTICLE_OPTION_DATA_SINGLE: articleOptionDataSchema,
    BEARER_TOKEN: bearerTokenSchema,
    TOPLEVELDOMAINS: topLevelDomainsSchema,
    DOMAINCHECK: domainCheckSchema,
    CARTSCHEMA: cartSchema,
    CUSTOMERCATEGORIES: customerCategoriesSchema,
    SOFTWARE: softwareDataSchema,
    DATABASE: databaseDataSchema,
    OPERATINGSYSTEMS: operatingSystemsDataSchema,
    PROMOTIONCODE: promotionCodeSchema,
    BANKACCOUNT: bankAccountSchema,
    PROMOTIONCODESTATUS: 'PROMOTIONCODE',
    STATUSTEXT: 'TEXT',
    STATUSCODE: 'STATUSCODE',
    CARTSHARESCHEMA: cartSchema,
    JSON: jsonSchema,
    RECOMMEND: jsonSchema,
    CLIENT_DATA: jsonSchema,
    ACCOUNTS: accountSchema,
    ACCOUNT: account,
    CONTRACTS: contractSchema,
    RESELLERS: resellerSchema,
}

export const callApi = (endpoint, schema, method, bearer, postData, customHeaders = {}) => {

    const fullUrl = (endpoint.indexOf(API_ROOT_DEFAULT) === -1 && endpoint.indexOf('http') === -1) ? API_ROOT_DEFAULT + endpoint : endpoint

    let requestHeaders = new Headers()
    requestHeaders.append('content-type', 'application/json');

    if (bearer) {
        requestHeaders.append('Authorization', `Bearer ${bearer}`);
    }

    if (customHeaders) {
        Object.keys(customHeaders).forEach(key => {
            requestHeaders.append(customHeaders[key].key, customHeaders[key].value);
        });
    }

    let fetchOtions = {method: method, headers: requestHeaders}

    if ((method === "POST" || method === "PUT") && postData) {
        fetchOtions.body = JSON.stringify(postData)
    }

    if (schema === Schemas.STATUSTEXT) {

        return fetch(fullUrl, fetchOtions)
            .then(response => {
                return response.statusText
            })

    } else if (schema === Schemas.STATUSCODE) {

        return fetch(fullUrl, fetchOtions)
            .then(response => {
                if (!response.ok) {
                    return Promise.reject({status: response.status})
                }
                return response.status
            })

    } else if (schema === Schemas.PROMOTIONCODESTATUS) {

        return fetch(fullUrl, fetchOtions)
            .then(response => {
                if (!response.ok) {
                    // code is not existing, return 400 status
                    if (response.status === 404) {
                        return Promise.reject({status: response.status, msg: Errors.PROMOTION_CODE_INVALID})
                    } else if (response.status === 400) {
                        return Promise.reject({status: response.status, msg: Errors.PROMOTION_CODE_FALSE_ARTICLES})
                    }
                }
                return response.json().catch(error => {
                    // error will be thrown when json() fails, it fails when code is valid
                    return Promise.resolve({status: 200})
                }).then((json, error) => {
                    if (json && json.msg) {
                        return Promise.reject({status: 404, msg: (json && json.msg) ? json.msg : Errors.PROMOTION_CODE_INVALID})
                    } else if (json.status === 200) {
                        return Promise.resolve({status: 200})
                    }
                })

            })


    } else if (schema === Schemas.BEARER_TOKEN) {

        return fetch(fullUrl, fetchOtions)
            .then(response =>
                response.json().then(json => {

                    if (response.status === 202) {
                        if(json.name === 'TOTP') {
                            return Promise.reject({status: response.status, msg: Errors.TOTP_REQUIRED})
                        } else if(json.name === 'DeviceCookie') {
                            setCookie('device_id', json.deviceId, 365);
                            return Promise.reject({status: response.status, msg: Errors.DEVICECOOKIE_REQUIRED})
                        }
                    } else if (!response.ok) {
                        return Promise.reject({status: response.status, msg: Errors.INVALID_CREDENTIALS})
                    }

                    let camelizedJson = camelizeKeys(json)

                    if (camelizedJson.data) {
                        camelizedJson = camelizedJson.data
                    }
                    return Object.assign({status: response.status},
                        normalize(camelizedJson, schema)
                    )
                })
            )

    } else {

        return fetch(fullUrl, fetchOtions)
            .then(response =>
                response.json().then(json => {
                    if (!response.ok) {
                        return Promise.reject(json)
                    }

                    let camelizedJson = camelizeKeys(json)

                    if (camelizedJson.data) {
                        camelizedJson = camelizedJson.data
                    }
                    return Object.assign({status: response.status},
                        normalize(camelizedJson, schema)
                    )
                })
            )

    }

}

export const MITTWALD_API = 'MITTWALD_API'

export default store => next => action => {
    const callAPI = action[MITTWALD_API]
    if (typeof callAPI === 'undefined') {
        return next(action)
    }

    let {endpoint} = callAPI
    const {schema, types, method, bearer, postData, payload, customHeaders} = callAPI

    if (typeof endpoint === 'function') {
        endpoint = endpoint(store.getState())
    }

    if (typeof endpoint !== 'string') {
        throw new Error('API Path needs to be specified.')
    }
    if (!schema) {
        throw new Error('Result schema needs to be specified.')
    }

    if (!Array.isArray(types) || types.length !== 3) {
        throw new Error('Expected an array of three action types.')
    }

    if (!types.every(type => typeof type === 'string')) {
        throw new Error('Expected action types to be strings.')
    }

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data)
        delete finalAction[MITTWALD_API]
        return finalAction
    }

    const [requestType, successType, failureType] = types
    next(actionWith({type: requestType, payload: payload}))

    return callApi(endpoint, schema, method, bearer, postData, customHeaders).then(
        response => next(actionWith({
            response,
            payload: payload,
            type: successType
        })),
        error => next(actionWith({
            payload: payload,
            status: error.status,
            type: failureType,
            error: error.msg || 'error'
        }))
    )
}



/** WEBPACK FOOTER **
 ** ./src/middleware/MittwaldApi.js
 **/